import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { 
  ArrowLeftIcon,
  ExclamationCircleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const ProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    product_name: '',
    product_code: '',
    description: '',
    category_id: '',
    price_book_item_id: '',
    is_active: true
  });
  const [categories, setCategories] = useState([]);
  const [priceBookItems, setPriceBookItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (isEditing && categories.length > 0) {
      fetchProduct();
    }
  }, [id, isEditing, categories]);

  const fetchInitialData = async () => {
    try {
      setInitialLoading(true);
      const [categoriesRes, priceBookRes] = await Promise.all([
        api.get('/products/categories'),
        api.get('/price-book/items')
      ]);
      
      setCategories(categoriesRes.data.filter(cat => cat.is_active));
      setPriceBookItems(priceBookRes.data.filter(item => item.is_active));
    } catch (err) {
      console.error('Error fetching initial data:', err);
      setError('Failed to load form data');
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchProduct = async () => {
    try {
      const response = await api.get(`/products/${id}`);
      setFormData({
        product_name: response.data.product_name || '',
        product_code: response.data.product_code || '',
        description: response.data.description || '',
        category_id: response.data.category_id || '',
        price_book_item_id: response.data.price_book_item_id || '',
        is_active: response.data.is_active !== false
      });
    } catch (err) {
      console.error('Error fetching product:', err);
      setError('Failed to load product details');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear messages when user starts typing
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.product_name.trim()) {
      setError('Product name is required');
      return;
    }

    if (!formData.category_id) {
      setError('Please select a category');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const payload = {
        product_name: formData.product_name.trim(),
        product_code: formData.product_code.trim() || null,
        description: formData.description.trim() || null,
        category_id: parseInt(formData.category_id),
        price_book_item_id: formData.price_book_item_id ? parseInt(formData.price_book_item_id) : null,
        is_active: formData.is_active
      };

      if (isEditing) {
        await api.put(`/products/${id}`, payload);
        setSuccess('Product updated successfully');
      } else {
        await api.post('/products', payload);
        setSuccess('Product created successfully');
      }

      // Redirect after a short delay to show success message
      setTimeout(() => {
        navigate('/products/list');
      }, 1500);

    } catch (err) {
      console.error('Error saving product:', err);
      setError(err.response?.data?.error || `Failed to ${isEditing ? 'update' : 'create'} product`);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/products/list')}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Products
          </button>
        </div>
        <h1 className="text-2xl font-semibold text-gray-900 mt-4">
          {isEditing ? 'Edit Product' : 'Add Product'}
        </h1>
        <p className="mt-2 text-sm text-gray-700">
          {isEditing ? 'Update the product details below.' : 'Create a new product for your catalog.'}
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="rounded-md bg-green-50 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">{success}</h3>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white shadow sm:rounded-lg">
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          {/* Product Name */}
          <div>
            <label htmlFor="product_name" className="block text-sm font-medium text-gray-700">
              Product Name *
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="product_name"
                id="product_name"
                required
                value={formData.product_name}
                onChange={handleInputChange}
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="e.g., Retirement Integration - Paystreams"
              />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              A specific, descriptive name for this product implementation
            </p>
          </div>

          {/* Product Code */}
          <div>
            <label htmlFor="product_code" className="block text-sm font-medium text-gray-700">
              Product Code
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="product_code"
                id="product_code"
                value={formData.product_code}
                onChange={handleInputChange}
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Optional product code"
              />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Optional code to identify this product
            </p>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <div className="mt-1">
              <textarea
                name="description"
                id="description"
                rows={3}
                value={formData.description}
                onChange={handleInputChange}
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Optional description of this product..."
              />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Optional description to help explain what this product does or how it differs from similar products
            </p>
          </div>

          {/* Category */}
          <div>
            <label htmlFor="category_id" className="block text-sm font-medium text-gray-700">
              Category *
            </label>
            <div className="mt-1">
              <select
                name="category_id"
                id="category_id"
                required
                value={formData.category_id}
                onChange={handleInputChange}
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              >
                <option value="">Select a category...</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.display_label}
                  </option>
                ))}
              </select>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Choose the category that best describes this product
            </p>
          </div>

          {/* Price Book Item */}
          <div>
            <label htmlFor="price_book_item_id" className="block text-sm font-medium text-gray-700">
              Price Book Item
            </label>
            <div className="mt-1">
              <select
                name="price_book_item_id"
                id="price_book_item_id"
                value={formData.price_book_item_id}
                onChange={handleInputChange}
                className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              >
                <option value="">No price book item linked...</option>
                {priceBookItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.item_name}
                  </option>
                ))}
              </select>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Optional: Link this product to a price book item for billing purposes
            </p>
          </div>

          {/* Active Status */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="is_active"
                name="is_active"
                type="checkbox"
                checked={formData.is_active}
                onChange={handleInputChange}
                className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="is_active" className="font-medium text-gray-700">
                Active
              </label>
              <p className="text-gray-500">
                Active products can be assigned to clients. Inactive products are hidden from selection.
              </p>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/products/list')}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  {isEditing ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditing ? 'Update Product' : 'Create Product'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm; 